import {
    Alert,
    DataTable,
    Icon,
    IconButton,
    Loading,
    SearchInput,
    StyleFunction,
    Surface,
    Text,
    useAuth,
    useMerchantConfig,
    usePagination,
    useThemedStyle,
} from "@venuepos/react-common";
import { useInvoicesQuery } from "graphql-sdk";
import { formatDateTime, numberFormat, parseAmount } from "lib";
import { formatAmount } from "lib/src/currencies/currencies";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";

import type { AvailableLocale } from "locales";
type ScreenProps = RootStackScreenProps<"INVOICES">;

export function InvoiceListScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.invoice");

    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "created_at",
        initialSortDirection: "DESC",
    });

    const [search, setSearch] = useState<string>("");
    const { data, loading, error } = useInvoicesQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
        fetchPolicy: "no-cache",
    });

    function handleShowInvoice(invoiceId: string) {
        navigate("INVOICE", {
            id: invoiceId,
        });
    }

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list (in case the user changed to a different page)
            onPageChange(0);
        },
        [onPageChange]
    );

    return (
        <AdminContainer>
            <SearchInput onChange={handleSearchTextChange} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "receipt_number" && sortDirection
                            }
                            onPress={() => onSortChange("receipt_number")}
                        >
                            {t(
                                "backoffice.invoice.receipt_number",
                                "Receipt number"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "shift_cashregister_name" &&
                                sortDirection
                            }
                            onPress={() =>
                                onSortChange("shift_cashregister_name")
                            }
                        >
                            {t(
                                "backoffice.invoice.cash_register",
                                "Cash Register"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            sortDirection={sortBy === "amount" && sortDirection}
                            onPress={() => onSortChange("amount")}
                        >
                            {t("backoffice.invoice.amount", "Amount")}
                        </DataTable.Title>
                        <DataTable.Title numeric>
                            {t("backoffice.invoice.item_count", "Items")}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            sortDirection={
                                sortBy === "created_at" && sortDirection
                            }
                            onPress={() => onSortChange("created_at")}
                        >
                            {t("backoffice.invoice.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.sortIcon} numeric>
                            <Icon color={styles.sortIcon.color} name="sort" />
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {t(
                                "receipt.error",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : !loading && data ? (
                        data?.invoices.data.map((invoice, invoiceIndex) => (
                            <DataTable.Row
                                onPress={() =>
                                    invoice && handleShowInvoice(invoice.id)
                                }
                                key={invoice?.id}
                                style={
                                    invoiceIndex % 2 === 1
                                        ? styles.oddRow
                                        : undefined
                                }
                            >
                                <DataTable.Cell>
                                    {invoice && invoice.receiptNumber}
                                </DataTable.Cell>
                                <DataTable.Cell>
                                    {invoice && invoice.shift.cashRegister.name}
                                </DataTable.Cell>
                                <DataTable.Cell numeric>
                                    {formatAmount(
                                        (invoice &&
                                            parseAmount(
                                                invoice.amount / 100
                                            )) ||
                                            0,
                                        currency,
                                        {
                                            locale: locale as AvailableLocale,
                                        }
                                    )}
                                </DataTable.Cell>
                                <DataTable.Cell numeric>
                                    <QuantityElement
                                        invoice={invoice}
                                        locale={locale}
                                    />
                                </DataTable.Cell>
                                <DataTable.Cell numeric>
                                    {invoice &&
                                        formatDateTime(
                                            invoice.createdAt,
                                            locale as AvailableLocale
                                        )}
                                </DataTable.Cell>
                                <DataTable.Cell style={styles.sortIcon} numeric>
                                    <IconButton
                                        color={styles.icon.color}
                                        name="order"
                                        onPress={() =>
                                            invoice &&
                                            handleShowInvoice(invoice.id)
                                        }
                                    />
                                </DataTable.Cell>
                            </DataTable.Row>
                        ))
                    ) : (
                        <Loading />
                    )}

                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.invoices.pagination.pages}
                        itemCount={data?.invoices.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    sortIcon: {
        color: theme.colors.black,
        flexBasis: theme.spacingScale * 6,
        flexGrow: 0,
        flexShrink: 0,
    },
    icon: {
        color: theme.colors.secondary,
    },
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
});

function QuantityElement({
    invoice,
    locale,
}: {
    invoice: any;
    locale: string;
}) {
    if (!invoice) {
        return null;
    }

    const quantity = invoice.items?.reduce(
        (sum: number, itr: { quantity: number }) => sum + itr.quantity,
        0
    );
    return (
        <Text>{numberFormat(Math.round(quantity * 100) / 100, 2, locale)}</Text>
    );
}
